import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { api, ApiError, type AuthUser, type RoleCode } from "../lib/api";
import { isRefereeAccount } from "./referee";
import {
  clearTokenReplacements,
  isTokenExpired,
  readTokenClaims,
  refreshTokenNeedsRotation,
  registerTokenReplacement,
  resolveLiveToken,
  sameTokenSubject,
  SESSION_EXPIRED_EVENT,
  shouldRefreshToken,
} from "../lib/sessionToken";
import { shouldPingVisit, tracksVisits, type VisitMark } from "../lib/visitPing";
import { purgePersonalCaches } from "../pwa/cache";
import { removePushSubscriptionOnLogout } from "../pwa/pushLogout";

const TOKEN_STORAGE_KEY="cymerg...oken";
const IMPERSONATION_RETURN_TOKEN_STORAGE_KEY = "cymergold-tenis-admin-return-token";
// Sesión larga del jugador (Eduardo 2026-10-06): token de renovación de 90
// días. Solo lo emite el servidor a cuentas sin rol de staff; «Salir» lo borra.
const REFRESH_TOKEN_STORAGE_KEY = "cymergold-tenis-refresh-token";
// Hora LOCAL (ms) en que este teléfono recibió el token de renovación: decide
// cuándo rotarlo sin depender del reloj del servidor (ver sessionToken).
const REFRESH_RECEIVED_AT_STORAGE_KEY = "cymergold-tenis-refresh-received-at";

export interface AuthState {
  status: "loading" | "authenticated" | "anonymous";
  token: string | null;
  user: AuthUser | null;
  login: (identifier: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  hasRole: (...roles: RoleCode[]) => boolean;
  isImpersonating: boolean;
  impersonateWithToken: (nextToken: string, nextUser: AuthUser) => void;
  stopImpersonation: () => Promise<void>;
  // adopta una sesión emitida por el backend (login, cambio de contraseña y
  // reset confirmado devuelven access_token+user frescos porque el cambio de
  // contraseña revoca el token del caller — no basta con actualizar el user)
  adoptSession: (data: { access_token: string; refresh_token?: string | null; user: AuthUser }) => void;
  /** La sesión caducó a media operación: se ofrece renovarla sin recargar. */
  sessionExpired?: boolean;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

function readStoredToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredToken(token: string | null): void {
  try {
    if (token) {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    // Storage disabled (private mode, etc.) — in-memory state still works.
  }
}

function readRefreshToken(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeRefreshToken(token: string | null | undefined): void {
  try {
    if (token) {
      window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
      window.localStorage.setItem(REFRESH_RECEIVED_AT_STORAGE_KEY, String(Date.now()));
    } else {
      window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      window.localStorage.removeItem(REFRESH_RECEIVED_AT_STORAGE_KEY);
    }
  } catch {
    // Storage disabled: la sesión dura lo que el token de acceso.
  }
}

function readRefreshReceivedAt(): number | null {
  try {
    const raw = window.localStorage.getItem(REFRESH_RECEIVED_AT_STORAGE_KEY);
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

type RenewOutcome =
  | { result: "ok"; accessToken: string; user: AuthUser }
  | { result: "rejected" | "failed" | "none" };

/** Canjea el token de renovación guardado. "ok" con la sesión nueva;
 * "rejected" si el servidor lo rechazó (se borra: hay que entrar con
 * contraseña); "failed" sin red (se conserva para el siguiente intento).
 *
 * `expectedSessionToken`: la pestaña ya tiene una sesión. El localStorage es
 * compartido entre pestañas: si en otra entró OTRA cuenta, su token de
 * renovación no debe colarse aquí. Se compara `sub` antes de canjear y otra
 * vez con el token nuevo; si difieren, esta pestaña no adopta nada ("none"). */
async function renewStoredSession(expectedSessionToken?: string | null): Promise<RenewOutcome> {
  const stored = readRefreshToken();
  if (!stored) return { result: "none" };
  if (expectedSessionToken && !sameTokenSubject(stored, expectedSessionToken)) return { result: "none" };
  try {
    const response = await api.auth.renew(stored);
    // El token rotado es de la misma cuenta que el guardado: se guarda siempre
    // (el anterior ya se canjeó), lo adopte o no esta pestaña.
    writeRefreshToken(response.data.refresh_token ?? null);
    if (expectedSessionToken && !sameTokenSubject(response.data.access_token, expectedSessionToken)) {
      return { result: "none" };
    }
    return { result: "ok", accessToken: response.data.access_token, user: response.data.user };
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      writeRefreshToken(null);
      return { result: "rejected" };
    }
    return { result: "failed" };
  }
}

function readReturnToken(): string | null {
  try {
    return window.localStorage.getItem(IMPERSONATION_RETURN_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeReturnToken(token: string | null): void {
  try {
    if (token) {
      window.localStorage.setItem(IMPERSONATION_RETURN_TOKEN_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(IMPERSONATION_RETURN_TOKEN_STORAGE_KEY);
    }
  } catch {
    // Storage disabled (private mode, etc.) — in-memory state still works.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => readStoredToken());
  const [returnToken, setReturnToken] = useState<string | null>(() => readReturnToken());
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>(
    token ? "loading" : "anonymous",
  );

  const [sessionExpired, setSessionExpired] = useState(false);

  const applyToken = useCallback((nextToken: string | null) => {
    setToken(nextToken);
    writeStoredToken(nextToken);
    setSessionExpired(false);
    if (nextToken === null) clearTokenReplacements();
  }, []);

  const applyReturnToken = useCallback((nextToken: string | null) => {
    setReturnToken(nextToken);
    writeReturnToken(nextToken);
  }, []);

  const loadCurrentUser = useCallback(
    async (currentToken: string) => {
      try {
        const response = await api.auth.me(currentToken);
        setUser(response.data.user);
        setStatus("authenticated");
      } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          // If a short-lived impersonation token expired, fall back to the still
          // valid original session instead of logging the real user out.
          const fallback = readReturnToken();
          if (fallback && fallback !== currentToken) {
            applyReturnToken(null);
            applyToken(fallback);
            return;
          }
          // Jugador con sesión larga: el token de acceso venció (abrió la app
          // días después) y se renueva sin pedir contraseña. Esto NO toca la
          // suscripción de avisos: solo «Salir» la borra.
          if (!fallback) {
            const renewed = await renewStoredSession(currentToken);
            if (renewed.result === "ok") {
              applyToken(renewed.accessToken);
              setUser(renewed.user);
              setStatus("authenticated");
              return;
            }
          }
          applyToken(null);
          setUser(null);
        }
        setStatus("anonymous");
      }
    },
    [applyToken, applyReturnToken],
  );

  // ¿Esta pestaña ya tuvo una sesión? Entonces, al quedarse sin token (venció
  // y no se pudo renovar, o «Salir»), NO adopta en silencio la sesión larga
  // guardada: en el localStorage compartido puede ser de otra cuenta que
  // entró en otra pestaña. Solo al ARRANCAR sin sesión se canjea.
  const hadSessionRef = useRef(false);
  if (token) hadSessionRef.current = true;

  useEffect(() => {
    if (!token) {
      setUser(null);
      if (!hadSessionRef.current && readRefreshToken() && !readReturnToken()) {
        // Arranque sin token de acceso pero con sesión larga: se renueva.
        let cancelled = false;
        setStatus("loading");
        void renewStoredSession().then((renewed) => {
          if (cancelled) return;
          if (renewed.result === "ok") {
            applyToken(renewed.accessToken);
            setUser(renewed.user);
            setStatus("authenticated");
          } else {
            setStatus("anonymous");
          }
        });
        return () => {
          cancelled = true;
        };
      }
      setStatus("anonymous");
      return;
    }
    setStatus("loading");
    void loadCurrentUser(token);
  }, [token, loadCurrentUser, applyToken]);

  const login = useCallback(
    async (identifier: string, password: string) => {
      const response = await api.auth.login(identifier, password);
      // Purga también al INICIAR sesión: cierra la ventana de respuestas en
      // vuelo que recrean el caché tras la purga del logout, y el caso de
      // sesión expirada (401) que nunca pasó por logout en un teléfono
      // compartido.
      void purgePersonalCaches();
      const nextUser = response.data.user;
      applyReturnToken(null);
      // Siempre se reescribe: otra cuenta en el mismo teléfono (o staff) no
      // hereda la sesión larga de la anterior.
      writeRefreshToken(response.data.refresh_token ?? null);
      applyToken(response.data.access_token);
      setUser(nextUser);
      setStatus("authenticated");
      return nextUser;
    },
    [applyReturnToken, applyToken],
  );

  const adoptSession = useCallback(
    (data: { access_token: string; refresh_token?: string | null; user: AuthUser }) => {
      void purgePersonalCaches();
      applyReturnToken(null);
      writeRefreshToken(data.refresh_token ?? null);
      applyToken(data.access_token);
      setUser(data.user);
      setStatus("authenticated");
    },
    [applyReturnToken, applyToken],
  );

  const logout = useCallback(async () => {
    // Token VIVO (tras renovaciones silenciosas el del estado puede estar
    // vencido) y capturado antes de applyToken(null), que borra la cadena de
    // renovaciones: si no, el servidor rechaza el borrado de la suscripción.
    const current = resolveLiveToken(token);
    // «Salir» explícito es lo ÚNICO que borra la suscripción de avisos (del
    // navegador y del servidor). Una sesión vencida o un 401 no la tocan.
    void removePushSubscriptionOnLogout(current);
    writeRefreshToken(null);
    applyReturnToken(null);
    applyToken(null);
    setUser(null);
    setStatus("anonymous");
    if (current) {
      try {
        await api.auth.logout(current);
      } catch {
        // Best-effort; stateless logout means the client simply discards the token.
      }
    }
    void purgePersonalCaches();
  }, [applyReturnToken, applyToken, token]);

  const refresh = useCallback(async () => {
    if (!token) return;
    await loadCurrentUser(token);
  }, [loadCurrentUser, token]);

  const hasRole = useCallback(
    (...roles: RoleCode[]) => {
      if (!user) return false;
      return roles.some((role) => user.roles.includes(role));
    },
    [user],
  );

  const impersonateWithToken = useCallback(
    (nextToken: string, nextUser: AuthUser) => {
      if (token) {
        applyReturnToken(returnToken ?? resolveLiveToken(token) ?? token);
      }
      applyToken(nextToken);
      setUser(nextUser);
      setStatus("authenticated");
    },
    [applyReturnToken, applyToken, returnToken, token],
  );

  const stopImpersonation = useCallback(async () => {
    const adminToken = returnToken;
    applyReturnToken(null);
    if (!adminToken) {
      applyToken(null);
      setUser(null);
      setStatus("anonymous");
      void purgePersonalCaches();
      return;
    }
    applyToken(adminToken);
    setStatus("loading");
    await loadCurrentUser(adminToken);
    void purgePersonalCaches();
  }, [applyReturnToken, applyToken, loadCurrentUser, returnToken]);

  // ---- Sesión que no caduca a media captura (Eduardo 2026-09-18) ----------
  // 1) Renovación silenciosa: invisible para React (ver lib/sessionToken) para
  //    que ninguna pantalla recargue datos y pise el formulario en curso.
  useEffect(() => {
    if (status !== "authenticated" || !token) return;
    let cancelled = false;
    const tick = async () => {
      const live = resolveLiveToken(token);
      if (!live || cancelled) return;
      // Sesión larga del jugador (nunca durante una suplantación): al abrir la
      // app se rota el token de renovación (deslizante) y, si el de acceso
      // venció o está por vencer, se cambia por uno nuevo sin contraseña.
      const impersonating = Boolean(returnToken) || readTokenClaims(live)?.impersonation === true;
      const stored = impersonating ? null : readRefreshToken();
      // Solo si es de ESTA cuenta: el de otra cuenta (otra pestaña) se ignora
      // y esta pestaña sigue con su propia política (p. ej. staff).
      const storedRefresh = stored && sameTokenSubject(stored, live) ? stored : null;
      if (storedRefresh) {
        const expired = isTokenExpired(live);
        if (!expired && !shouldRefreshToken(live) && !refreshTokenNeedsRotation(readRefreshReceivedAt())) return;
        const renewed = await renewStoredSession(live);
        if (cancelled) return;
        if (renewed.result === "ok") {
          registerTokenReplacement(live, renewed.accessToken);
          writeStoredToken(renewed.accessToken);
          setSessionExpired(false);
        } else if (expired) {
          setSessionExpired(true);
        }
        return;
      }
      if (isTokenExpired(live)) {
        setSessionExpired(true);
        return;
      }
      if (!shouldRefreshToken(live)) return;
      try {
        const response = await api.auth.refresh(live);
        if (cancelled) return;
        registerTokenReplacement(live, response.data.access_token);
        writeStoredToken(response.data.access_token);
        // Jugador que entró antes de la sesión larga: aquí recibe su token
        // de renovación (el servidor solo lo da a cuentas sin rol de staff).
        if (
          response.data.refresh_token &&
          !impersonating &&
          sameTokenSubject(response.data.refresh_token, live)
        ) {
          writeRefreshToken(response.data.refresh_token);
        }
        setSessionExpired(false);
      } catch {
        // Sin red o rechazada: el siguiente intento, o el 401 real, avisan.
      }
    };
    void tick();
    const interval = window.setInterval(() => void tick(), 5 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [status, token, returnToken]);

  // 2) Un 401 con sesión abierta = caducó: aviso en pantalla, sin recargar.
  useEffect(() => {
    const onExpired = () => {
      if (status === "authenticated") setSessionExpired(true);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [status]);

  // 3) Si la MISMA persona inicia sesión en otra pestaña, esta adopta ese
  //    token sin recargar: basta volver a pulsar Guardar.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== TOKEN_STORAGE_KEY || !event.newValue || !token) return;
      const live = resolveLiveToken(token);
      if (!live || event.newValue === live) return;
      const mine = readTokenClaims(live)?.sub;
      const theirs = readTokenClaims(event.newValue)?.sub;
      if (!mine || mine !== theirs) return; // otra cuenta: no se mezcla
      registerTokenReplacement(live, event.newValue);
      setSessionExpired(false);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [token]);

  // 4) Último ingreso (Eduardo 2026-10-03): la renovación de (1) NO lo marca
  //    (una pestaña olvidada se renueva sola), y abrir la app tampoco: tras
  //    cada publicación las pestañas abiertas se recargan solas (UpdatePrompt)
  //    y una pantalla olvidada a la vista parecería «entrar» cada día. Se avisa
  //    con el primer clic o tecla y luego a lo más cada 15 min (lib/visitPing);
  //    solo el staff y nunca viendo la app como otra cuenta. La marca vive en
  //    un ref para que volver a correr el efecto (StrictMode, cambio de token)
  //    no repita el aviso.
  const lastVisitRef = useRef<VisitMark | null>(null);
  const isTrackedStaff = tracksVisits(user?.roles);
  useEffect(() => {
    if (status !== "authenticated" || !token || returnToken || !isTrackedStaff) return;
    const claims = readTokenClaims(token);
    const sub = claims?.sub;
    if (!sub || claims?.impersonation === true) return;
    const ping = () => {
      const now = Date.now();
      if (!shouldPingVisit(lastVisitRef.current, sub, now)) return;
      const live = resolveLiveToken(token);
      // Vencida: no se avisa; el «Tu sesión caducó» lo da una acción real.
      if (!live || isTokenExpired(live, now)) return;
      lastVisitRef.current = { sub, at: now };
      api.auth.visit(live).catch(() => {
        // Sin red: el siguiente clic pasados 15 min vuelve a avisar.
      });
    };
    const options = { capture: true, passive: true } as const;
    window.addEventListener("pointerdown", ping, options);
    window.addEventListener("keydown", ping, options);
    return () => {
      window.removeEventListener("pointerdown", ping, options);
      window.removeEventListener("keydown", ping, options);
    };
  }, [status, token, returnToken, isTrackedStaff]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      token,
      user,
      login,
      logout,
      refresh,
      hasRole,
      isImpersonating: Boolean(returnToken),
      impersonateWithToken,
      stopImpersonation,
      adoptSession,
      sessionExpired,
    }),
    [
      status,
      token,
      user,
      login,
      logout,
      refresh,
      hasRole,
      returnToken,
      impersonateWithToken,
      stopImpersonation,
      adoptSession,
      sessionExpired,
    ],
  );

  const loginHref = typeof window !== "undefined" && window.location.pathname.startsWith("/tenis/") ? "/tenis/login" : "/login";

  return (
    <AuthContext.Provider value={value}>
      {sessionExpired && status === "authenticated" ? (
        <div className="session-expired-banner" role="alert">
          <strong>Tu sesión caducó.</strong>
          <span>
            No recargues esta página para no perder lo que escribiste: inicia sesión en otra pestaña y vuelve a
            pulsar Guardar aquí.
          </span>
          <a className="btn btn--compact" href={loginHref} target="_blank" rel="noreferrer">
            Iniciar sesión en otra pestaña
          </a>
        </div>
      ) : null}
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

/** Como useAuth, pero sin exigir <AuthProvider>: para piezas públicas (el
 * encabezado de torneos123.com) que solo ajustan un detalle si hay sesión y
 * también se montan sueltas. Sin proveedor devuelve undefined (= sin sesión). */
export function useOptionalAuth(): AuthState | undefined {
  return useContext(AuthContext);
}

// "club" outranks "admin": club staff carry both roles (the admin one powers
// the tournament panel) but their home is the club panel.
export const ROLE_PRIORITY: RoleCode[] = ["superadmin", "club", "admin", "player"];

export function homePathForUser(user: AuthUser): string {
  if (isRefereeAccount(user)) return "/arbitro";
  for (const role of ROLE_PRIORITY) {
    if (user.roles.includes(role)) {
      switch (role) {
        case "superadmin":
          return "/superadmin";
        case "club":
          return "/club";
        case "admin":
          return "/admin";
        case "player":
          return "/player";
      }
    }
  }
  return "/login";
}
